/**
 * Combat engine (house-rule model, 2026-07-02). See docs/GAME-RULES.md.
 *  - Ships fight enemy ships in the 6 ADJACENT hexes (hexDist === 1), not on a
 *    shared tile — under one-per-tile two ships can never share a hex.
 *  - Each ship fires ONCE per combat phase at a single adjacent enemy.
 *  - Larger ships fire first; damage applies immediately (sunk before firing =
 *    never fires). Combat is mutual (both neighbours damage each other).
 *  - transports are protected while a friendly warship is also adjacent to the
 *    attacker (an escort draws fire ahead of the transport).
 *  - carrier air support: +1 dmg per friendly CV within 1 tile of the attacker
 *    (stacks; not vs submerged subs; a carrier doesn't boost itself).
 *  - submerged-sub immunity is in the DMG chart via defKey (BB→SS = 0, etc.).
 * A "combat site" for the WP-8 cinematic is now a connected CLUSTER of adjacent
 * fighting ships rather than one hex. Pure: no UI/audio.
 */
import { hexDist } from './board';
import { atkKey, defKey, DMG, FIRE_ORDER, isTransportClass, UNITS } from './ships';
import { type GameState, type Ship, type ShipType } from './state';

export interface CombatEvent {
  attackerId: number;
  attackerType: string;
  attackerSide: 'R' | 'Y';
  targetId: number;
  targetType: string;
  targetSide: 'R' | 'Y';
  dmg: number;
  airBonus: number;
  sunk: boolean;
}

/** Pre-combat snapshot of a ship at a site — enough for the WP-8 cinematic to
 *  draw it (sprite, livery, HP bar) without reaching back into the live G. */
export interface CombatShipSnapshot {
  id: number;
  type: ShipType;
  side: 'R' | 'Y';
  hp: number;
  maxHp: number;
  submerged: boolean;
}

/** One battle cluster: every ship engaged in it before combat + the ordered
 *  shots that fired. `c,r` is a representative member's coords (the cinematic
 *  uses it only for a HUD label). The engine has already mutated G by the time
 *  you hold one (sunk ships removed). */
export interface CombatSite {
  c: number;
  r: number;
  /** every ship in the cluster (both sides) as it stood when combat opened */
  before: CombatShipSnapshot[];
  events: CombatEvent[];
}

/** Live enemy ships adjacent to `s` (hexDist === 1). */
export function adjacentEnemies(g: GameState, s: Ship): Ship[] {
  return g.ships.filter(
    (x) => x.side !== s.side && x.hp > 0 && hexDist(x.c, x.r, s.c, s.r) === 1,
  );
}

/**
 * Battle clusters: connected components of the "engaged" graph. A participant is
 * any ship with at least one adjacent enemy; two participants share a cluster if
 * they are adjacent (either side). A lone duel is a 2-ship cluster; a melee of
 * escorts around a target is one cluster. Replaces the old contestedHexes().
 */
export function combatClusters(g: GameState): Ship[][] {
  const participants = g.ships.filter((s) => s.hp > 0 && adjacentEnemies(g, s).length > 0);
  const seen = new Set<number>();
  const clusters: Ship[][] = [];
  for (const start of participants) {
    if (seen.has(start.id)) continue;
    const members: Ship[] = [];
    const stack = [start];
    seen.add(start.id);
    while (stack.length) {
      const cur = stack.pop()!;
      members.push(cur);
      for (const other of participants) {
        if (seen.has(other.id)) continue;
        if (hexDist(other.c, other.r, cur.c, cur.r) === 1) {
          seen.add(other.id);
          stack.push(other);
        }
      }
    }
    clusters.push(members);
  }
  return clusters;
}

/** Damage `a` deals to `t`, including carrier air support. */
export function dmgVal(g: GameState, a: Ship, t: Ship): number {
  let d = DMG[atkKey(a)][defKey(t)] ?? 0;
  if (d > 0 && !(t.type === 'SS' && t.submerged)) {
    const cvs = g.ships.filter(
      (s) =>
        s.side === a.side && s.type === 'CV' && s.id !== a.id && s.hp > 0 &&
        hexDist(s.c, s.r, a.c, a.r) <= 1,
    ).length;
    d += cvs;
  }
  return d;
}

export function airBonus(g: GameState, a: Ship, t: Ship): number {
  const base = DMG[atkKey(a)][defKey(t)] ?? 0;
  if (base <= 0 || (t.type === 'SS' && t.submerged)) return 0;
  return dmgVal(g, a, t) - base;
}

/**
 * Legal targets for `attacker`: enemies in ADJACENT tiles. Transports are
 * protected while a warship is also adjacent to the attacker (escorts draw fire
 * first); among those, prefer targets it can actually damage.
 */
export function validTargets(g: GameState, attacker: Ship): Ship[] {
  const here = adjacentEnemies(g, attacker);
  const escorts = here.filter((x) => !isTransportClass(x));
  const pool = escorts.length ? escorts : here;
  const damageable = pool.filter((t) => dmgVal(g, attacker, t) > 0);
  return damageable.length ? damageable : pool;
}

/** Default target choice (used for AI sides and as fallback for the player). */
export function pickTarget(g: GameState, a: Ship): Ship | null {
  const pool = validTargets(g, a);
  if (pool.length === 0) return null;
  let best: Ship | null = null;
  let bestScore = -1;
  for (const t of pool) {
    const d = dmgVal(g, a, t);
    if (d <= 0) continue;
    let sc = d;
    if (d >= t.hp) sc += 50 + UNITS[t.type].cost; // finishing blow
    sc += UNITS[t.type].cost * 0.3;
    if (isTransportClass(t)) sc += 8; // transports score VPs — kill them
    if (sc > bestScore) {
      bestScore = sc;
      best = t;
    }
  }
  return best ?? pool[0] ?? null;
}

/**
 * Resolve one combat round for a cluster of adjacent fighters. `playerTargets`
 * maps attacker id → chosen target id (re-picked automatically if the choice is
 * already sunk or no longer adjacent). Mutates G (hp, fought flags, sinking) and
 * returns the event list. Members are sorted so larger ships fire first.
 */
export function resolveCluster(
  g: GameState, members: readonly Ship[], playerTargets?: Record<number, number>,
): CombatEvent[] {
  const shooters = [...members].sort((a, b) => FIRE_ORDER[a.type] - FIRE_ORDER[b.type]);
  const events: CombatEvent[] = [];
  // Resolve in fire-order TIERS. Larger ships still fire (and can sink) before
  // smaller ones, but ships of the SAME class fire SIMULTANEOUSLY: within a tier,
  // every shooter picks its target and rolls damage against the HP as it stood
  // when the tier OPENED, then all that damage lands together. This kills the old
  // side-bias where same-class ties fell back to g.ships insertion order (which
  // correlates with side), handing that side a free first-strike win in every
  // mirror duel (DD-vs-DD etc.) — mutual destruction is now possible and fair.
  const pickFor = (s: Ship): Ship | undefined => {
    const chosen = playerTargets?.[s.id];
    if (chosen !== undefined) {
      const t = g.ships.find((x) => x.id === chosen);
      // honour the pick only if still a legal (adjacent, alive) target
      if (t && t.hp > 0 && hexDist(t.c, t.r, s.c, s.r) === 1) return t;
    }
    return pickTarget(g, s) ?? undefined;
  };
  for (let i = 0; i < shooters.length; ) {
    const tier = FIRE_ORDER[shooters[i]!.type];
    const pending: { s: Ship; t: Ship; d: number; ab: number }[] = [];
    let j = i;
    for (; j < shooters.length && FIRE_ORDER[shooters[j]!.type] === tier; j++) {
      const s = shooters[j]!;
      if (s.hp <= 0) continue; // sunk by an earlier (larger) tier — never fires
      const t = pickFor(s);
      if (!t || t.hp <= 0) continue;
      s.fought = true;
      t.fought = true;
      const d = dmgVal(g, s, t);
      if (d > 0) pending.push({ s, t, d, ab: airBonus(g, s, t) });
    }
    // land this tier's shots together (a same-tier peer's death can't cancel them)
    for (const p of pending) {
      p.t.hp -= p.d;
      events.push({
        attackerId: p.s.id, attackerType: p.s.type, attackerSide: p.s.side,
        targetId: p.t.id, targetType: p.t.type, targetSide: p.t.side,
        dmg: p.d, airBonus: p.ab, sunk: p.t.hp <= 0,
      });
    }
    i = j;
  }
  g.ships = g.ships.filter((x) => x.hp > 0);
  return events;
}

/**
 * Resolve every battle cluster once, returning per-cluster results (a pre-combat
 * ship snapshot + the ordered events) for the cinematic. Mutates G; this is the
 * structured form of the combat phase — resolveCombat() in turn.ts flattens it.
 */
export function collectCombatSites(
  g: GameState, playerTargets?: Record<number, number>,
): CombatSite[] {
  const sites: CombatSite[] = [];
  // Snapshot clusters first — resolveCluster mutates g.ships.
  const clusters = combatClusters(g);
  const snapshots = clusters.map((members) =>
    members.map((s) => ({
      id: s.id, type: s.type, side: s.side, hp: s.hp, maxHp: UNITS[s.type].hp,
      submerged: s.submerged,
    })),
  );
  clusters.forEach((members, i) => {
    const before = snapshots[i]!;
    const anchor = members[0]!;
    const events = resolveCluster(g, members, playerTargets);
    sites.push({ c: anchor.c, r: anchor.r, before, events });
  });
  return sites;
}
